import React, { useState, useEffect, useRef, useCallback } from 'react';
import '@google/model-viewer';
import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { PluginConfigProps } from '../../types/PluginInterface';
import { useFileManager } from '../../hooks/useFileManager';
import { usePaymentApiKeys } from '../../hooks/usePaymentApiKeys';
import { ALL_COUNTRIES } from '../../data/countries';
import AutoTranslateText from '../../components/AutoTranslateText';
import { EcommerceConfig, Product, Order, ProductCategory, EcommerceNotificationSettings, ProductAttribute, ProductAttributeOption } from './types';
import { apiKeyService } from '../../services/apiKeyService';
import { defaultConfig } from './config';
import { Editor } from '@tinymce/tinymce-react';
import { withTabLink } from '../../utils/tinymceTabLink';
import { useTinyMceApiKey } from '../../hooks/useTinyMceApiKey';
import PayPalOrdersPanel from '../../components/PayPalOrdersPanel';
import StripeOrdersPanel from '../../components/StripeOrdersPanel';
import { useGoogleMapsApiKey } from '../../hooks/useGoogleMapsApiKey';
import { api } from '../../services/api';

// Déclaration TS du custom element <model-viewer>
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'model-viewer': any;
    }
  }
}

const FbxPreview: React.FC<{ url: string; width?: number; height?: number; className?: string; style?: React.CSSProperties }> = ({
  url,
  width,
  height = 80,
  className,
  style
}) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const modelRef = useRef<THREE.Object3D | null>(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const w = width || mount.clientWidth || 80;
    const h = height;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f5f5);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 1000);
    camera.position.set(2.2, 1.8, 3.0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(w, h);
    rendererRef.current = renderer;
    mount.appendChild(renderer.domElement);

    scene.add(new THREE.AmbientLight(0xffffff, 0.9));
    const dir = new THREE.DirectionalLight(0xffffff, 0.6);
    dir.position.set(5, 10, 7.5);
    scene.add(dir);

    const loader = new FBXLoader();
    loader.load(
      url,
      (obj) => {
        modelRef.current = obj;
        const box = new THREE.Box3().setFromObject(obj);
        const size = new THREE.Vector3();
        box.getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z) || 1;
        const scale = 2 / maxDim;
        obj.scale.setScalar(scale);
        const center = new THREE.Vector3();
        box.getCenter(center);
        obj.position.sub(center.multiplyScalar(scale));
        scene.add(obj);
      },
      undefined,
      (err) => console.error('Erreur FBX preview:', err)
    );

    const animate = () => {
      if (modelRef.current) modelRef.current.rotation.y += 0.01;
      renderer.render(scene, camera);
      animRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      if (rendererRef.current) {
        rendererRef.current.dispose();
        if (rendererRef.current.domElement.parentElement) {
          rendererRef.current.domElement.parentElement.removeChild(rendererRef.current.domElement);
        }
      }
      if (sceneRef.current && modelRef.current) sceneRef.current.remove(modelRef.current);
    };
  }, [url, width, height]);

  return <div ref={mountRef} className={className} style={{ width: width || '100%', height, ...style }} />;
};

export const EcommerceConfigComponent: React.FC<PluginConfigProps> = ({ 
  config, 
  onConfigChange 
}) => {
  const currentConfig = config as EcommerceConfig;
  const { openFileManager } = useFileManager();
  const { isLoading, error, hasStripeKey, hasPaypalKey } = usePaymentApiKeys();
  
  const { apiKey: tinymceApiKey, isLoading: isApiKeyLoading } = useTinyMceApiKey();
  const { apiKey: googleMapsApiKey, isLoading: isMapsKeyLoading } = useGoogleMapsApiKey();

  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [activeCardImageIndex, setActiveCardImageIndex] = useState<Record<string, number>>({});
  const [emailTemplateType, setEmailTemplateType] = useState<'buyer_shipping' | 'buyer_collect' | 'seller'>('buyer_shipping');

  // ✅ TOUS les onglets sont conservés ici
  const [activeTab, setActiveTab] = useState<'general' | 'products' | 'payment' | 'appearance' | 'settings' | 'confirmations' | 'legal' | 'sales' | 'paypal' | 'stripe'>('general');
  const [productCategoryFilter, setProductCategoryFilter] = useState<string>('all');
  const [hasWhatsapp, setHasWhatsapp] = useState(false);

  // ── Ventes state ──
  interface StoredOrder {
    id: string;
    status: string;
    deliveryMethod: string;
    createdAt: string;
    orderData: Order;
  }
  const [salesOrders, setSalesOrders] = useState<StoredOrder[]>([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesError, setSalesError] = useState<string | null>(null);
  const [salesFrom, setSalesFrom] = useState('');
  const [salesTo, setSalesTo] = useState('');
  const [shipTrackingInputs, setShipTrackingInputs] = useState<Record<string, string>>({});
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});

  const fetchSales = useCallback(async () => {
    setSalesLoading(true);
    setSalesError(null);
    try {
      const params = new URLSearchParams();
      if (salesFrom) params.set('from', salesFrom);
      if (salesTo) params.set('to', salesTo);
      if ((config as EcommerceConfig).id) params.set('shopId', (config as EcommerceConfig).id!);
      const resp: any = await api.get(`/ecommerce/orders?${params.toString()}`);
      setSalesOrders(resp?.orders || []);
    } catch {
      setSalesError('Impossible de charger les ventes.');
    } finally {
      setSalesLoading(false);
    }
  }, [salesFrom, salesTo, config]);

  useEffect(() => {
    if (activeTab === 'sales') fetchSales();
  }, [activeTab, fetchSales]);

  useEffect(() => {
    apiKeyService.getApiKeys().then((keys) => {
      const activeKeys = keys.filter((k: any) => k.isActive);
      const svcs = new Set(activeKeys.map((k: any) => k.service.toLowerCase()));
      const providers = new Set(activeKeys.map((k: any) => (k.provider || '').toLowerCase()));
      setHasWhatsapp(svcs.has('whatsapp_phone_id') && svcs.has('whatsapp_token') || providers.has('whatsapp'));
    }).catch(() => {});
  }, []);

  const DEFAULT_NOTIF: EcommerceNotificationSettings = {
    notifFlexihubSeller: true,
    notifFlexihubClient: false,
    emailClient: true,
    emailSeller: true,
    whatsappClient: false,
    whatsappSeller: false,
    whatsappClientTemplate: 'Commande {{orderId}} confirmée. Total : {{orderTotal}}. Merci – {{shopName}}',
    whatsappSellerTemplate: 'Nouvelle vente #{{orderId}} de {{customerName}} — {{orderTotal}}.',
  };
  const notif: EcommerceNotificationSettings = { ...DEFAULT_NOTIF, ...(currentConfig.notificationSettings || {}) };
  const updateNotif = (partial: Partial<EcommerceNotificationSettings>) =>
    updateConfig({ notificationSettings: { ...notif, ...partial } });

  const handleShip = async (order: StoredOrder) => {
    setActionLoading(p => ({ ...p, [order.id]: true }));
    try {
      await api.post(`/ecommerce/orders/${order.id}/ship`, {
        config: currentConfig,
        trackingNumber: shipTrackingInputs[order.id] || '',
      });
      fetchSales();
    } catch {
      alert('Erreur lors de l\'expédition.');
    } finally {
      setActionLoading(p => ({ ...p, [order.id]: false }));
    }
  };

  const handleCollected = async (order: StoredOrder) => {
    setActionLoading(p => ({ ...p, [order.id]: true }));
    try {
      await api.post(`/ecommerce/orders/${order.id}/collected`, { config: currentConfig });
      fetchSales();
    } catch {
      alert('Erreur lors du marquage.');
    } finally {
      setActionLoading(p => ({ ...p, [order.id]: false }));
    }
  };

  const exportCsv = () => {
    const rows = [
      ['ID', 'Date', 'Client', 'Email', 'Mode livraison', 'Paiement', 'Statut', 'Total'],
      ...salesOrders.map(o => {
        const d = o.orderData;
        return [
          o.id,
          new Date(o.createdAt).toLocaleDateString('fr-FR'),
          d.customerInfo?.name || '',
          d.customerInfo?.email || '',
          o.deliveryMethod === 'click_and_collect' ? 'Click & Collect' : 'Expédition',
          d.paymentMethod || '',
          o.status,
          (d.total ?? 0).toFixed(2),
        ];
      }),
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const period = salesFrom || salesTo ? `_${salesFrom || ''}_${salesTo || ''}` : '';
    link.download = `ventes${period}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const initialProductState: Partial<Product> = {
    name: '',
    price: 0,
    description: '',
    image: '',
    category: '',
    stock: 0,
    rating: 5,
    tags: [],
    shippingCost: 0,
    images: [],
    attributes: []
  };

  const [newProduct, setNewProduct] = useState<Partial<Product>>(initialProductState);

  const updateConfig = (updates: Partial<EcommerceConfig>) => {
    const newConfig = { ...config, ...updates };
    if (updates.shopName !== undefined) {
      newConfig.title = updates.shopName || defaultConfig.shopName;
    }
    if (updates.shopDescription !== undefined) {
      newConfig.description = updates.shopDescription || `Boutique avec ${newConfig.products?.length || 0} produits.`;
    }
    onConfigChange(newConfig);
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) return;
    if ((config as any).ownerId || (config as any).ownerEmail) return;
    try {
      const userData = JSON.parse(storedUser);
      const ownerId = userData?.id || userData?.userId;
      const ownerEmail = userData?.email;
      if (ownerId || ownerEmail) {
        updateConfig({
          ...(ownerId ? { ownerId: String(ownerId) } : {}),
          ...(ownerEmail ? { ownerEmail } : {})
        });
      }
    } catch {
      // ignore
    }
  }, [config, updateConfig]);

  const openLogoSelector = () => {
    openFileManager({
      onFileSelect: (fileInfo: { url: string }) => {
        if (fileInfo && fileInfo.url) {
          updateConfig({ shopLogoUrl: fileInfo.url });
        }
      },
      type: 'image'
    });
  };

  const isVideoUrl = (url: string) => /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(url.split('?')[0].split('#')[0]);
  const isGlbUrl = (url: string) => /\.(glb|gltf)$/i.test(url.split('?')[0].split('#')[0]);
  const isFbxUrl = (url: string) => /\.fbx$/i.test(url.split('?')[0].split('#')[0]);

  const addNewProductImage = () => {
    openFileManager({
      onFileSelect: (fileInfo: { url: string }) => {
        if (fileInfo?.url) {
          setNewProduct(prev => {
            const imgs = Array.from(new Set([...(prev.images || []), fileInfo.url]));
            return {
              ...prev,
              images: imgs,
              image: imgs[0] || prev.image
            };
          });
        }
      },
      type: 'all'
    });
  };

  const removeNewProductImage = (url: string) => {
    setNewProduct(prev => {
      const filtered = (prev.images || []).filter(i => i !== url);
      return {
        ...prev,
        images: filtered,
        image: filtered[0] || ''
      };
    });
  };

  const setPrimaryNewProductImage = (url: string) => {
    setNewProduct(prev => {
      const others = (prev.images || []).filter(i => i !== url);
      const reordered = [url, ...others];
      return {
        ...prev,
        images: reordered,
        image: url
      };
    });
  };

  const saveProduct = () => {
    if (!newProduct.name || !newProduct.price) return;

    const images = (newProduct.images && newProduct.images.length)
      ? newProduct.images
      : (newProduct.image ? [newProduct.image] : []);

    // Spread all fields from newProduct so nothing (badge, salePrice, taxRate…) is silently dropped,
    // then explicitly override the fields that need normalisation.
    const product: Product = {
      ...(newProduct as Product),
      id: editingProduct?.id || `product_${Date.now()}`,
      name: newProduct.name || '',
      price: newProduct.price || 0,
      description: newProduct.description || '',
      image: images[0] || newProduct.image || '/produit.png',
      images,
      category: newProduct.category || 'Général',
      stock: newProduct.stock ?? 0,
      rating: newProduct.rating ?? 5,
      tags: newProduct.tags || [],
      shippingCost: newProduct.shippingCost ?? 0,
    };

    const products = editingProduct
      ? (currentConfig.products || []).map(p => p.id === editingProduct.id ? product : p)
      : [...(config.products || []), product];

    // Keep categories as-is (managed separately); product.category is just a name string
    updateConfig({ products });
    setEditingProduct(null);
    setNewProduct(initialProductState);
  };

  const fillExample = () => {
    onConfigChange({
      ...currentConfig,
      shopName: 'Boutique Démo', shopDescription: 'Exemple de boutique avec 3 produits.',
      products: [
        { id: '1', name: 'Livre de cuisine', price: 19.99, description: 'Un livre pour cuisiner comme un chef.', image: '/livre.jpg', category: 'Livres', stock: 12, rating: 5, tags: ['livre', 'cuisine'], shippingCost: 0 },
        { id: '2', name: 'T-shirt coton', price: 14.99, description: 'T-shirt 100% coton bio.', image: '/teeshirt.jpg', category: 'Vêtements', stock: 30, rating: 4, tags: ['tshirt', 'vêtement'], shippingCost: 0 },
        { id: '3', name: 'Smartphone X', price: 399.99, description: 'Smartphone performant et abordable.', image: '/iphone.jpg', category: 'Électronique', stock: 7, rating: 4, tags: ['smartphone', 'électronique'], shippingCost: 0 }
      ],
      categories: [
        { id: 'cat-livres', name: 'Livres', color: '#3b82f6', active: true },
        { id: 'cat-vetements', name: 'Vêtements', color: '#ec4899', active: true },
        { id: 'cat-electronique', name: 'Électronique', color: '#f59e0b', active: true },
      ] as ProductCategory[],
    });
  };

  if (isLoading) {
    return <div className="text-center p-3"><div className="spinner-border spinner-border-sm" /> Chargement des clés API...</div>;
  }
  if (error) {
    return <div className="alert alert-danger">Erreur: {error}</div>;
  }

  const getProductImages = (p: Product): string[] => {
    if (p.images && p.images.length) return p.images;
    return p.image ? [p.image] : [];
  };

  const startEditProduct = (product: Product) => {
    const images = getProductImages(product);
    setEditingProduct(product);
    setNewProduct({
      ...product,
      images,
      image: images[0] || product.image || ''
    });
  };

  const cancelEditProduct = () => {
    setEditingProduct(null);
    setNewProduct(initialProductState);
  };

  // ✅ Helper pour afficher un ColorPicker avec option Transparent
  const renderColorInput = (label: string, configKey: keyof EcommerceConfig, defaultColor: string = '#ffffff') => {
    const currentValue = (config[configKey] as string);
    const isTransparent = currentValue === 'transparent';
    const pickerValue = isTransparent ? defaultColor : (currentValue || defaultColor);

    return (
      <div className="mb-3">
        <label className="form-label small text-muted">{label}</label>
        <div className="d-flex align-items-center gap-2">
          <input 
            type="color" 
            className="form-control form-control-color" 
            value={pickerValue}
            disabled={isTransparent}
            onChange={(e) => updateConfig({ [configKey]: e.target.value })}
            title="Choisir une couleur"
          />
          <div className="form-check mb-0 bg-white border rounded px-2 py-1">
             <input 
                className="form-check-input" 
                type="checkbox" 
                id={`chk-${configKey}`}
                checked={isTransparent}
                onChange={(e) => updateConfig({ [configKey]: e.target.checked ? 'transparent' : pickerValue })}
             />
             <label className="form-check-label small cursor-pointer" htmlFor={`chk-${configKey}`}>Transparent</label>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="ecommerce-config p-3">
      <div className="mb-3">
        <button type="button" className="btn btn-outline-info btn-sm" onClick={fillExample}>🛒 Exemple boutique</button>
      </div>
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item"><button className={`nav-link ${activeTab === 'general' ? 'active' : ''}`} onClick={() => setActiveTab('general')}>🏪 Général</button></li>
        <li className="nav-item"><button className={`nav-link ${activeTab === 'products' ? 'active' : ''}`} onClick={() => setActiveTab('products')}>📦 Produits</button></li>
        <li className="nav-item"><button className={`nav-link ${activeTab === 'payment' ? 'active' : ''}`} onClick={() => setActiveTab('payment')}>💳 Paiement</button></li>
        <li className="nav-item"><button className={`nav-link ${activeTab === 'appearance' ? 'active' : ''}`} onClick={() => setActiveTab('appearance')}>🎨 Apparence</button></li>
        <li className="nav-item"><button className={`nav-link ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>⚙️ Paramètres</button></li>
        <li className="nav-item"><button className={`nav-link ${activeTab === 'confirmations' ? 'active' : ''}`} onClick={() => setActiveTab('confirmations')}>📬 Confirmations</button></li>
        <li className="nav-item"><button className={`nav-link ${activeTab === 'legal' ? 'active' : ''}`} onClick={() => setActiveTab('legal')}>📄 CGU/RGPD</button></li>
        <li className="nav-item"><button className={`nav-link ${activeTab === 'sales' ? 'active' : ''}`} onClick={() => setActiveTab('sales')}>📊 Ventes</button></li>
        {hasPaypalKey && <li className="nav-item"><button className={`nav-link ${activeTab === 'paypal' ? 'active' : ''}`} onClick={() => setActiveTab('paypal')}>🅿 PayPal</button></li>}
        {hasStripeKey && <li className="nav-item"><button className={`nav-link ${activeTab === 'stripe' ? 'active' : ''}`} onClick={() => setActiveTab('stripe')}>💜 Stripe</button></li>}
      </ul>

      {activeTab === 'general' && (
        <div className="space-y-3">
          <div className="mb-3">
            <label className="form-label"><AutoTranslateText sourceLang="fr">Nom de la boutique</AutoTranslateText></label>
            <input type="text" className="form-control" value={config.shopName || ''} onChange={(e) => updateConfig({ shopName: e.target.value })} placeholder="Ma Boutique en Ligne" />
          </div>
          <div className="mb-3">
            <label className="form-label"><AutoTranslateText sourceLang="fr">Logo de la boutique</AutoTranslateText></label>
            <div className="input-group">
              <input type="text" className="form-control" value={currentConfig.shopLogoUrl || ''} onChange={(e) => updateConfig({ shopLogoUrl: e.target.value })} placeholder="URL du logo ou sélectionnez" />
              <button className="btn btn-outline-secondary" type="button" onClick={openLogoSelector}>📁</button>
              {currentConfig.shopLogoUrl && (
                <button
                  className="btn btn-outline-danger"
                  type="button"
                  onClick={() => updateConfig({ shopLogoUrl: '', shopLogoWidth: undefined, shopLogoHeight: undefined })}
                  title="Supprimer le logo"
                >
                  🗑️
                </button>
              )}
            </div>
            {currentConfig.shopLogoUrl && (
              <div className="mt-2 d-flex flex-wrap align-items-center gap-3">
                <img
                  src={currentConfig.shopLogoUrl}
                  alt="Aperçu du logo"
                  style={{
                    width: currentConfig.shopLogoWidth ? `${currentConfig.shopLogoWidth}px` : 'auto',
                    height: currentConfig.shopLogoHeight ? `${currentConfig.shopLogoHeight}px` : 'auto',
                    objectFit: 'contain',
                    backgroundColor: '#fff'
                  }}
                  className="img-thumbnail"
                />
                <div className="d-flex flex-column gap-2" style={{ minWidth: 220 }}>
                  <div>
                    <label className="form-label small mb-1"><AutoTranslateText sourceLang="fr">Largeur (px)</AutoTranslateText></label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="range"
                        min={40}
                        max={600}
                        step={5}
                        className="form-range"
                        value={currentConfig.shopLogoWidth ?? 140}
                        onChange={(e) => updateConfig({ shopLogoWidth: Number(e.target.value) })}
                      />
                      <span className="badge bg-light text-dark border">{currentConfig.shopLogoWidth ?? 140}px</span>
                    </div>
                  </div>
                  <div>
                    <label className="form-label small mb-1"><AutoTranslateText sourceLang="fr">Hauteur (px)</AutoTranslateText></label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="range"
                        min={20}
                        max={300}
                        step={5}
                        className="form-range"
                        value={currentConfig.shopLogoHeight ?? 80}
                        onChange={(e) => updateConfig({ shopLogoHeight: Number(e.target.value) })}
                      />
                      <span className="badge bg-light text-dark border">{currentConfig.shopLogoHeight ?? 80}px</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="mb-3">
            <label className="form-label"><AutoTranslateText sourceLang="fr">Description</AutoTranslateText></label>
            {isApiKeyLoading ? (
              <div className="text-muted small">Chargement de l'éditeur...</div>
            ) : !tinymceApiKey ? (
              <textarea
                className="form-control"
                rows={3}
                value={config.shopDescription || ''}
                onChange={(e) => updateConfig({ shopDescription: e.target.value })}
                placeholder="Description de votre boutique..."
              />
            ) : (
              <Editor
                apiKey={tinymceApiKey}
                value={config.shopDescription || ''}
                init={withTabLink({
                  height: 200,
                  menubar: false,
                  plugins: [
                    'autolink', 'lists', 'link', 'image', 'charmap', 'preview', 'anchor',
                    'searchreplace', 'visualblocks', 'code', 'fullscreen',
                    'insertdatetime', 'media', 'table', 'wordcount'
                  ],
                  toolbar: 'undo redo | fontfamily fontsize | forecolor backcolor | formatselect | bold italic underline strikethrough | ' +
                    'alignleft aligncenter alignright alignjustify | ' +
                    'bullist numlist outdent indent | removeformat | ' +
                    'link image media | table | code fullscreen',
                  font_family_formats: 'Arial=arial,helvetica,sans-serif;Times New Roman=times new roman,times;Courier New=courier new,courier;Georgia=georgia,palatino;Verdana=verdana,geneva',
                  content_style: `
                    body { font-family:Helvetica,Arial,sans-serif; font-size:14px; }
                    img[style*="float: left"] { margin: 0 12px 12px 0; }
                    img[style*="float: right"] { margin: 0 0 12px 12px; }
                  `,
                  language: 'fr',
                  branding: false,
                  allow_script_urls: true,
                  allow_html_data_urls: true,
                  allow_events: true,
                  extended_valid_elements: 'div[*],span[*],i[*],button[*]',
                  custom_elements: 'button,i',
                  valid_children: '+body[div|button]',
                  valid_elements: '*[*]',
                  paste_data_images: true,
                  paste_as_text: false,
                  paste_retain_style_properties: 'all',
                  entity_encoding: 'raw'
                })}
                onEditorChange={(content) => updateConfig({ shopDescription: content })}
              />
            )}
          </div>
          <div className="mb-3">
            <label className="form-label"><AutoTranslateText sourceLang="fr">Devise</AutoTranslateText></label>
            <select className="form-select" value={config.currency || 'EUR'} onChange={(e) => updateConfig({ currency: e.target.value })}>
              <option value="EUR">€ Euro</option>
              <option value="USD">$ Dollar US</option>
              <option value="GBP">£ Livre Sterling</option>
              <option value="CAD">$ Dollar Canadien</option>
            </select>
          </div>
          {/* ❌ "Produits par page" supprimé ici comme demandé */}
        </div>
      )}

      {activeTab === 'products' && (
        <div className="space-y-3">

          {/* ── Catégories ── */}
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">🏷️ <AutoTranslateText sourceLang="fr">Catégories</AutoTranslateText></h6>
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => {
                const newCat: ProductCategory = { id: `cat-${Date.now().toString(36)}`, name: 'Nouvelle catégorie', color: '#6366f1', active: true };
                updateConfig({ categories: [...((currentConfig.categories || []) as ProductCategory[]), newCat] });
              }}>+ Ajouter</button>
            </div>
            <div className="card-body">
              {((currentConfig.categories || []) as ProductCategory[]).length === 0 && (
                <div className="text-muted small">Aucune catégorie. Ajoutez-en pour organiser vos produits.</div>
              )}
              <div className="d-flex flex-column gap-2">
                {((currentConfig.categories || []) as ProductCategory[]).map((cat, idx) => (
                  <div key={cat.id} className="d-flex align-items-center gap-2 border rounded p-2">
                    <input type="color" className="form-control form-control-color flex-shrink-0" style={{ width: 36, height: 32 }}
                      value={cat.color || '#6366f1'}
                      onChange={(e) => {
                        const cats = [...((currentConfig.categories || []) as ProductCategory[])];
                        cats[idx] = { ...cats[idx], color: e.target.value };
                        updateConfig({ categories: cats });
                      }} />
                    <input type="text" className="form-control form-control-sm" placeholder="Nom" value={cat.name}
                      onChange={(e) => {
                        const cats = [...((currentConfig.categories || []) as ProductCategory[])];
                        cats[idx] = { ...cats[idx], name: e.target.value };
                        updateConfig({ categories: cats });
                      }} />
                    <input type="text" className="form-control form-control-sm" placeholder="Badge (ex: 🔥)" style={{ maxWidth: 90 }} value={cat.badge || ''}
                      onChange={(e) => {
                        const cats = [...((currentConfig.categories || []) as ProductCategory[])];
                        cats[idx] = { ...cats[idx], badge: e.target.value || undefined };
                        updateConfig({ categories: cats });
                      }} />
                    <div className="form-check mb-0 flex-shrink-0">
                      <input className="form-check-input" type="checkbox" checked={cat.active !== false}
                        onChange={(e) => {
                          const cats = [...((currentConfig.categories || []) as ProductCategory[])];
                          cats[idx] = { ...cats[idx], active: e.target.checked };
                          updateConfig({ categories: cats });
                        }} />
                      <label className="form-check-label small">Actif</label>
                    </div>
                    <button type="button" className="btn btn-sm btn-outline-danger flex-shrink-0"
                      onClick={() => {
                        const cats = ((currentConfig.categories || []) as ProductCategory[]).filter((_, i) => i !== idx);
                        updateConfig({ categories: cats });
                      }}>🗑️</button>
                  </div>
                ))}
              </div>

              {((currentConfig.categories || []) as ProductCategory[]).filter(c => c.active !== false).length > 0 && (
                <div className="row mt-3 g-3">
                  <div className="col-md-6">
                    <label className="form-label small"><AutoTranslateText sourceLang="fr">Catégorie affichée par défaut (visiteur)</AutoTranslateText></label>
                    <select className="form-select form-select-sm"
                      value={currentConfig.defaultCategory || 'all'}
                      onChange={(e) => updateConfig({ defaultCategory: e.target.value })}>
                      <option value="all">Toutes</option>
                      {((currentConfig.categories || []) as ProductCategory[]).filter(c => c.active !== false).map(c => (
                        <option key={c.id} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small"><AutoTranslateText sourceLang="fr">Vue par défaut (visiteur)</AutoTranslateText></label>
                    <div className="d-flex gap-2">
                      {([
                        { value: 'grid', label: '⊞ Grille' },
                        { value: 'list', label: '≡ Liste' },
                        { value: 'carousel', label: '▷ Carousel' },
                      ] as const).map(({ value, label }) => (
                        <button key={value} type="button"
                          className={`btn btn-sm flex-grow-1 ${(currentConfig.displayMode || 'grid') === value ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => updateConfig({ displayMode: value })}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Filtre pour la liste des produits existants */}
              {((currentConfig.categories || []) as ProductCategory[]).filter(c => c.active !== false).length > 0 && (currentConfig.products || []).length > 0 && (
                <div className="mt-3">
                  <label className="form-label small text-muted"><AutoTranslateText sourceLang="fr">Filtrer les produits par catégorie</AutoTranslateText></label>
                  <select className="form-select form-select-sm" value={productCategoryFilter} onChange={(e) => setProductCategoryFilter(e.target.value)}>
                    <option value="all">Toutes</option>
                    {((currentConfig.categories || []) as ProductCategory[]).filter(c => c.active !== false).map(c => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">
                <AutoTranslateText sourceLang="fr">
                  {editingProduct ? '✏️ Modifier un produit' : '➕ Ajouter un produit'}
                </AutoTranslateText>
              </h5>
            </div>
            <div className="card-body">
              {editingProduct && (
                <div className="alert alert-info py-2">
                  <strong>Édition :</strong> {editingProduct.name}
                </div>
              )}
              {/* Le contenu de l'ajout produit reste identique */}
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label"><AutoTranslateText sourceLang="fr">Nom du produit</AutoTranslateText></label>
                  <input type="text" className="form-control" value={newProduct.name || ''} onChange={(e) => setNewProduct({...newProduct, name: e.target.value})} placeholder="Nom du produit" />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label"><AutoTranslateText sourceLang="fr">Prix</AutoTranslateText></label>
                  <input type="number" step="0.01" className="form-control" value={newProduct.price || 0} onChange={(e) => setNewProduct({...newProduct, price: parseFloat(e.target.value)})} placeholder="0.00" />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label"><AutoTranslateText sourceLang="fr">Description</AutoTranslateText></label>
                {isApiKeyLoading ? (
                  <div className="text-muted small">Chargement de l'éditeur...</div>
                ) : !tinymceApiKey ? (
                  <textarea
                    className="form-control"
                    rows={2}
                    value={newProduct.description || ''}
                    onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                    placeholder="Description du produit"
                  />
                ) : (
                  <Editor
                    apiKey={tinymceApiKey}
                    value={newProduct.description || ''}
                    onEditorChange={(content) => setNewProduct((prev) => ({ ...prev, description: content }))}
                    init={withTabLink({
                      height: 180,
                      menubar: false,
                      plugins: [
                        'autolink', 'lists', 'link', 'charmap', 'preview', 'anchor',
                        'searchreplace', 'visualblocks', 'code', 'fullscreen',
                        'insertdatetime', 'media', 'table', 'wordcount'
                      ],
                      toolbar: 'undo redo | fontfamily fontsize | forecolor backcolor | formatselect | bold italic underline strikethrough | ' +
                        'alignleft aligncenter alignright alignjustify | ' +
                        'bullist numlist outdent indent | removeformat | ' +
                        'link | table | code fullscreen',
                      font_family_formats: 'Arial=arial,helvetica,sans-serif;Times New Roman=times new roman,times;Courier New=courier new,courier;Georgia=georgia,palatino;Verdana=verdana,geneva',
                      content_style: `
                        body { font-family:Helvetica,Arial,sans-serif; font-size:14px; }
                        img[style*="float: left"] { margin: 0 12px 12px 0; }
                        img[style*="float: right"] { margin: 0 0 12px 12px; }
                      `,
                      language: 'fr',
                      branding: false,
                      paste_data_images: true,
                      paste_as_text: false,
                      paste_retain_style_properties: 'all'
                    })}
                  />
                )}
              </div>
              {/* Le reste des champs produits (Images, cat, stock...) */}
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label"><AutoTranslateText sourceLang="fr">Images / Vidéos du produit</AutoTranslateText></label>
                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex gap-2">
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addNewProductImage}>
                        <AutoTranslateText sourceLang="fr">➕ Ajouter média</AutoTranslateText>
                      </button>
                      {newProduct.images && newProduct.images.length > 0 && (
                        <button type="button" className="btn btn-outline-warning btn-sm" onClick={() => setNewProduct(p => ({ ...p, images: [], image: '' }))}>
                          Réinitialiser
                        </button>
                      )}
                    </div>
                    {newProduct.images && newProduct.images.length > 0 && (
                      <div className="d-flex flex-wrap gap-2">
                        {newProduct.images.map((media) => (
                          <div key={media} className="position-relative">
                            {isVideoUrl(media) ? (
                              <video
                                src={media}
                                className="rounded border"
                                style={{ width: 60, height: 60, objectFit: 'cover' }}
                                muted
                                playsInline
                              />
                            ) : isGlbUrl(media) ? (
                              <model-viewer
                                src={media}
                                style={{ width: 60, height: 60, background: '#f5f5f5', borderRadius: 6 }}
                                camera-controls
                                auto-rotate
                                exposure="1"
                                shadow-intensity="0.6"
                              />
                            ) : isFbxUrl(media) ? (
                              <FbxPreview
                                url={media}
                                width={60}
                                height={60}
                                className="rounded border"
                              />
                            ) : (
                              <img
                                src={media}
                                alt="media"
                                className="rounded border"
                                style={{ width: 60, height: 60, objectFit: 'cover' }}
                              />
                            )}
                            <button
                              type="button"
                              className="btn btn-danger btn-sm p-0 position-absolute top-0 end-0 rounded-circle"
                              style={{ width: 18, height: 18, lineHeight: 1, fontSize: 10, transform: 'translate(30%, -30%)' }}
                              onClick={() => removeNewProductImage(media)}
                            >
                              ×
                            </button>
                            <button
                              type="button"
                              className="btn btn-dark btn-sm p-0 position-absolute bottom-0 start-0 rounded-circle"
                              style={{ width: 18, height: 18, lineHeight: 1, fontSize: 10, transform: 'translate(-30%, 30%)' }}
                              title="Définir comme principal"
                              onClick={() => setPrimaryNewProductImage(media)}
                            >
                              ★
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label"><AutoTranslateText sourceLang="fr">Catégorie</AutoTranslateText></label>
                  {(currentConfig.categories || []).length > 0 ? (
                    <select className="form-select" value={newProduct.category || ''} onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}>
                      <option value="">Sans catégorie</option>
                      {((currentConfig.categories || []) as ProductCategory[]).filter((c) => c.active !== false).map((c) => (
                        <option key={c.id} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" className="form-control" value={newProduct.category || ''} onChange={(e) => setNewProduct({...newProduct, category: e.target.value})} placeholder="Électronique, Vêtements..." />
                  )}
                </div>
              </div>
              <div className="row">
                  <div className="col-md-4 mb-3"><label className="form-label">Stock</label><input type="number" className="form-control" value={newProduct.stock || 0} onChange={e=>setNewProduct({...newProduct, stock: parseInt(e.target.value)})} /></div>
                  <div className="col-md-4 mb-3"><label className="form-label">Frais Port</label><input type="number" className="form-control" value={newProduct.shippingCost || 0} onChange={e=>setNewProduct({...newProduct, shippingCost: parseFloat(e.target.value)})} /></div>
                  <div className="col-md-4 mb-3"><label className="form-label">Note</label><input type="number" className="form-control" min="1" max="5" value={newProduct.rating || 5} onChange={e=>setNewProduct({...newProduct, rating: parseInt(e.target.value)})} /></div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label small">Prix promo (barré)</label>
                  <div className="input-group">
                    <input type="number" step="0.01" className="form-control" placeholder="Prix avant promo" value={(newProduct as any).salePrice || ''} onChange={e => setNewProduct({ ...newProduct, salePrice: e.target.value ? parseFloat(e.target.value) : undefined } as any)} />
                    <span className="input-group-text">→ promo</span>
                  </div>
                  <small className="text-muted">Si renseigné, le prix original sera barré et ce prix affiché en rouge.</small>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label small">Badge produit</label>
                  <select className="form-select" value={(newProduct as any).badge || ''} onChange={e => setNewProduct({ ...newProduct, badge: e.target.value || undefined } as any)}>
                    <option value="">Aucun badge</option>
                    <option value="new">✨ NOUVEAU</option>
                    <option value="sale">🏷️ PROMO</option>
                    <option value="hot">🔥 POPULAIRE</option>
                  </select>
                </div>
              </div>
              {/* ─── Attributs produit ─── */}
              <div className="mb-3" style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: 12 }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label mb-0 fw-semibold">Attributs (couleurs, tailles…)</label>
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => {
                    const attr: ProductAttribute = {
                      id: `attr_${Date.now()}`,
                      name: 'Nouvelle option',
                      type: 'text',
                      required: false,
                      options: []
                    };
                    setNewProduct(prev => ({ ...prev, attributes: [...(prev.attributes || []), attr] }));
                  }}>+ Attribut</button>
                </div>
                {((newProduct.attributes || []) as ProductAttribute[]).map((attr, ai) => (
                  <div key={attr.id} style={{ background: '#f8f9fa', borderRadius: 6, padding: 10, marginBottom: 8 }}>
                    <div className="row g-2 mb-2 align-items-center">
                      <div className="col-md-4">
                        <input type="text" className="form-control form-control-sm" placeholder="Nom (ex: Couleur)" value={attr.name}
                          onChange={e => setNewProduct(prev => {
                            const attrs = [...(prev.attributes || [])] as ProductAttribute[];
                            attrs[ai] = { ...attrs[ai], name: e.target.value };
                            return { ...prev, attributes: attrs };
                          })} />
                      </div>
                      <div className="col-md-3">
                        <select className="form-select form-select-sm" value={attr.type}
                          onChange={e => setNewProduct(prev => {
                            const attrs = [...(prev.attributes || [])] as ProductAttribute[];
                            attrs[ai] = { ...attrs[ai], type: e.target.value as ProductAttribute['type'] };
                            return { ...prev, attributes: attrs };
                          })}>
                          <option value="text">Texte</option>
                          <option value="color">Couleur</option>
                          <option value="size">Taille</option>
                        </select>
                      </div>
                      <div className="col-md-3">
                        <div className="form-check form-switch mt-1">
                          <input className="form-check-input" type="checkbox" checked={attr.required}
                            onChange={e => setNewProduct(prev => {
                              const attrs = [...(prev.attributes || [])] as ProductAttribute[];
                              attrs[ai] = { ...attrs[ai], required: e.target.checked };
                              return { ...prev, attributes: attrs };
                            })} id={`req-${attr.id}`} />
                          <label className="form-check-label small" htmlFor={`req-${attr.id}`}>Obligatoire</label>
                        </div>
                      </div>
                      <div className="col-md-2 text-end">
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => {
                          setNewProduct(prev => ({ ...prev, attributes: (prev.attributes || []).filter((_, i) => i !== ai) }));
                        }}>✕</button>
                      </div>
                    </div>
                    {/* Options de l'attribut */}
                    <div className="d-flex flex-wrap gap-2 mb-1">
                      {(attr.options || []).map((opt, oi) => (
                        <div key={opt.id} className="d-flex align-items-center gap-1" style={{ background: '#fff', border: '1px solid #ced4da', borderRadius: 4, padding: '2px 6px' }}>
                          {attr.type === 'color' && (
                            <input type="color" value={opt.color || '#000000'} style={{ width: 20, height: 20, border: 'none', padding: 0, cursor: 'pointer' }}
                              onChange={e => setNewProduct(prev => {
                                const attrs = [...(prev.attributes || [])] as ProductAttribute[];
                                const opts = [...(attrs[ai].options || [])];
                                opts[oi] = { ...opts[oi], color: e.target.value };
                                attrs[ai] = { ...attrs[ai], options: opts };
                                return { ...prev, attributes: attrs };
                              })} />
                          )}
                          <input type="text" style={{ width: attr.type === 'color' ? 70 : 90, fontSize: 12, border: 'none', outline: 'none' }}
                            value={opt.label} placeholder="Label"
                            onChange={e => setNewProduct(prev => {
                              const attrs = [...(prev.attributes || [])] as ProductAttribute[];
                              const opts = [...(attrs[ai].options || [])];
                              opts[oi] = { ...opts[oi], label: e.target.value };
                              attrs[ai] = { ...attrs[ai], options: opts };
                              return { ...prev, attributes: attrs };
                            })} />
                          <input type="number" step="0.01" style={{ width: 55, fontSize: 11, border: 'none', outline: 'none', color: '#6c757d' }}
                            value={opt.priceAdjustment ?? ''} placeholder="±€"
                            onChange={e => setNewProduct(prev => {
                              const attrs = [...(prev.attributes || [])] as ProductAttribute[];
                              const opts = [...(attrs[ai].options || [])];
                              opts[oi] = { ...opts[oi], priceAdjustment: e.target.value !== '' ? parseFloat(e.target.value) : undefined };
                              attrs[ai] = { ...attrs[ai], options: opts };
                              return { ...prev, attributes: attrs };
                            })} />
                          <button type="button" style={{ background: 'none', border: 'none', fontSize: 10, color: '#dc3545', cursor: 'pointer', padding: 0 }}
                            onClick={() => setNewProduct(prev => {
                              const attrs = [...(prev.attributes || [])] as ProductAttribute[];
                              attrs[ai] = { ...attrs[ai], options: attrs[ai].options.filter((_, i) => i !== oi) };
                              return { ...prev, attributes: attrs };
                            })}>✕</button>
                        </div>
                      ))}
                      <button type="button" className="btn btn-sm btn-outline-secondary" style={{ fontSize: 11, padding: '1px 6px' }}
                        onClick={() => {
                          const newOpt: ProductAttributeOption = { id: `opt_${Date.now()}`, label: '' };
                          setNewProduct(prev => {
                            const attrs = [...(prev.attributes || [])] as ProductAttribute[];
                            attrs[ai] = { ...attrs[ai], options: [...attrs[ai].options, newOpt] };
                            return { ...prev, attributes: attrs };
                          });
                        }}>+ option</button>
                    </div>
                  </div>
                ))}
                {!(newProduct.attributes?.length) && (
                  <small className="text-muted">Aucun attribut. Cliquez sur "+ Attribut" pour ajouter des options (couleur, taille, etc.).</small>
                )}
              </div>

              <div className="d-flex gap-2">
                <button type="button" className="btn btn-primary" onClick={saveProduct} disabled={!newProduct.name || !newProduct.price}>
                  <AutoTranslateText sourceLang="fr">{editingProduct ? '💾 Mettre à jour' : '➕ Ajouter le produit'}</AutoTranslateText>
                </button>
                {editingProduct && (
                  <button type="button" className="btn btn-outline-secondary" onClick={cancelEditProduct}>
                    Annuler
                  </button>
                )}
              </div>
            </div>
          </div>
          {currentConfig.products && currentConfig.products.length > 0 && (
            <div className="mt-4">
              <h6 className="mb-3">Produits existants ({currentConfig.products.length})</h6>
              <div className="d-flex overflow-auto pb-3 gap-3">
                {currentConfig.products.filter((product: Product) => productCategoryFilter === 'all' || product.category === productCategoryFilter).map((product: Product) => (
                   <div key={product.id} className="card flex-shrink-0" style={{ width: '250px' }}>
                     <div style={{ height: '150px', overflow:'hidden', position:'relative' }}>
                        {(() => {
                          const media = (product.images?.[0]) || product.image || '/produit.png';
                          return isVideoUrl(media) ? (
                            <video src={media} className="w-100 h-100 object-fit-cover" muted playsInline />
                          ) : isGlbUrl(media) ? (
                            <model-viewer
                              src={media}
                              style={{ width: '100%', height: '100%', background: '#f5f5f5' }}
                              camera-controls
                              auto-rotate
                              exposure="1"
                              shadow-intensity="0.6"
                            />
                          ) : isFbxUrl(media) ? (
                            <FbxPreview url={media} height={150} className="w-100 h-100" />
                          ) : (
                            <img src={media} className="w-100 h-100 object-fit-cover" alt={product.name} />
                          );
                        })()}
                     </div>
                     <div className="card-body p-2">
                        <div className="fw-bold text-truncate">{product.name}</div>
                        <div className="text-primary">{product.price} {currentConfig.currency}</div>
                        <div className="d-flex gap-1 mt-2">
                          <button className="btn btn-sm btn-outline-primary w-100" onClick={() => startEditProduct(product)}>Modifier</button>
                          <button className="btn btn-sm btn-outline-danger w-100" onClick={() => {
                               const products = currentConfig.products?.filter(p => p.id !== product.id) || [];
                               updateConfig({ products });
                          }}>Supprimer</button>
                        </div>
                     </div>
                   </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'payment' && (
        <div className="space-y-3">
           <div className="alert alert-info">Configuration des méthodes de paiement</div>
           <div className="form-check">
              <input className="form-check-input" type="checkbox" checked={config.paymentMethods?.stripe || false} onChange={(e) => updateConfig({ paymentMethods: { ...config.paymentMethods, stripe: e.target.checked } })} />
              <label className="form-check-label">Stripe</label>
           </div>
           <div className="form-check">
              <input className="form-check-input" type="checkbox" checked={config.paymentMethods?.paypal || false} onChange={(e) => updateConfig({ paymentMethods: { ...config.paymentMethods, paypal: e.target.checked } })} />
              <label className="form-check-label">PayPal</label>
           </div>
           <div className="form-check">
              <input className="form-check-input" type="checkbox" checked={config.paymentMethods?.cash || false} onChange={(e) => updateConfig({ paymentMethods: { ...config.paymentMethods, cash: e.target.checked } })} />
              <label className="form-check-label">Paiement à la livraison</label>
           </div>
        </div>
      )}

      {/* ✅ ONGLET APPARENCE REDESSINÉ SELON VOS INSTRUCTIONS */}
      {activeTab === 'appearance' && (
        <div className="animate-fade-in">
          
          {/* Section Fond & Texte */}
          <div className="card mb-3 border-light bg-light">
             <div className="card-body">
                <h6 className="card-title text-muted mb-3">Fond & Typographie</h6>
                
                {/* 🎨 Couleur de fond du plugin + Transparent */}
                <div className="mb-3">
                    <label className="form-label">Couleur de fond du plugin</label>
                    <div className="d-flex align-items-center gap-3">
                        <input 
                            type="color" 
                            className="form-control form-control-color" 
                            title="Choisir une couleur"
                            value={((currentConfig.backgroundColor ?? currentConfig.background) === 'transparent' ? '#ffffff' : (currentConfig.backgroundColor ?? currentConfig.background) ?? '#ffffff')} 
                            disabled={(currentConfig.backgroundColor ?? currentConfig.background) === 'transparent'}
                            onChange={(e) => updateConfig({ backgroundColor: e.target.value, background: e.target.value })}
                        />
                        <div className="form-check mb-0">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id="bgTransparent"
                                checked={(currentConfig.backgroundColor ?? currentConfig.background) === 'transparent'}
                                onChange={(e) => {
                                    const val = e.target.checked ? 'transparent' : '#ffffff';
                                    updateConfig({ backgroundColor: val, background: val });
                                }}
                            />
                            <label className="form-check-label" htmlFor="bgTransparent">Fond transparent</label>
                        </div>
                    </div>
                </div>

                <div className="row g-2">
                    <div className="col-md-6">
                        <label className="form-label small">Couleur du texte principal</label>
                        <input type="color" className="form-control form-control-color w-100" value={currentConfig.textColor || '#212529'} onChange={(e) => updateConfig({ textColor: e.target.value })} />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label small">Police</label>
                        <select className="form-select" value={currentConfig.fontFamily || 'inherit'} onChange={(e) => updateConfig({ fontFamily: e.target.value })}>
                            <option value="inherit">Par défaut</option>
                            <option value="Arial, sans-serif">Arial</option>
                            <option value="'Helvetica Neue', Helvetica, sans-serif">Helvetica</option>
                            <option value="'Times New Roman', serif">Times New Roman</option>
                            <option value="'Courier New', monospace">Courier New</option>
                            <option value="Georgia, serif">Georgia</option>
                            <option value="'Verdana', sans-serif">Verdana</option>
                        </select>
                    </div>
                </div>
             </div>
          </div>

          {/* ✅ NOUVEAU : Configuration des zones spécifiques (Header, Produit, Footer, Titre, Description) */}
          <div className="card mb-3 border-light bg-light">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="card-title text-muted mb-0">Couleurs des zones</h6>
                <span className="badge bg-light text-secondary border">Zones clés</span>
              </div>
              <div className="row g-3">
                <div className="col-md-4">
                  {renderColorInput("En-tête (Recherche & Panier)", "headerBackgroundColor", "#ffffff")}
                </div>
                <div className="col-md-4">
                  {renderColorInput("Fiche Produit (Fond)", "productBackgroundColor", "#ffffff")}
                </div>
                <div className="col-md-4">
                  {renderColorInput("Pied de page (Statistiques)", "footerBackgroundColor", "#ffffff")}
                </div>
                <div className="col-md-6">
                  {renderColorInput("Titre (fond)", "titleBackgroundColor", "#ffffff")}
                </div>
                <div className="col-md-6">
                  {renderColorInput("Description (fond)", "shopDescriptionBackgroundColor", "#ffffff")}
                </div>
              </div>
            </div>
          </div>

          {/* Section Titre */}
          <div className="card mb-3 border-light bg-light">
             <div className="card-body">
                <h6 className="card-title text-muted mb-3">Titre de la Boutique</h6>
                <div className="row g-2 align-items-end">
                    <div className="col-md-4">
                        <label className="form-label small">Couleur Titre</label>
                        <input type="color" className="form-control form-control-color w-100" value={currentConfig.titleColor || '#0d6efd'} onChange={(e) => updateConfig({ titleColor: e.target.value })} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label small">Taille Titre</label>
                        <select className="form-select" value={currentConfig.titleSize || '2rem'} onChange={(e) => updateConfig({ titleSize: e.target.value })}>
                            <option value="1.5rem">Petit (1.5rem)</option>
                            <option value="2rem">Moyen (2rem)</option>
                            <option value="3rem">Grand (3rem)</option>
                            <option value="4rem">Très Grand (4rem)</option>
                        </select>
                    </div>
                    <div className="col-md-4">
                        <div className="d-flex gap-2">
                            <button 
                                className={`btn btn-sm flex-grow-1 ${currentConfig.titleBold ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                onClick={() => updateConfig({ titleBold: !currentConfig.titleBold })}
                            >
                                Gras
                            </button>
                            <button 
                                className={`btn btn-sm flex-grow-1 ${currentConfig.titleItalic ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                onClick={() => updateConfig({ titleItalic: !currentConfig.titleItalic })}
                            >
                                Italique
                            </button>
                        </div>
                    </div>
                </div>
             </div>
          </div>

          {/* ✅ FUSIONNÉ & RENOMMÉ : Style Fiche Produit (Description + Prix + Boutons) */}
          <div className="card mb-3 border-light bg-light">
            <div className="card-body">
              <h6 className="card-title text-muted mb-3">Style Fiche Produit</h6>
              
              {/* --- 1. CONFIGURATION DESCRIPTION PRODUIT --- */}
              <h6 className="small text-muted border-bottom pb-1 mb-2">Description</h6>
              <div className="row g-2 align-items-end mb-3">
                <div className="col-md-3">
                  <label className="form-label small">Couleur</label>
                  <input
                    type="color"
                    className="form-control form-control-color w-100"
                    value={currentConfig.descriptionColor || '#6c757d'}
                    onChange={(e) => updateConfig({ descriptionColor: e.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label small">Taille</label>
                  <select
                    className="form-select"
                    value={currentConfig.descriptionSize || '0.95rem'}
                    onChange={(e) => updateConfig({ descriptionSize: e.target.value })}
                  >
                    <option value="0.85rem">Petite (0.85rem)</option>
                    <option value="0.95rem">Standard (0.95rem)</option>
                    <option value="1.05rem">Moyenne (1.05rem)</option>
                    <option value="1.15rem">Grande (1.15rem)</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <button
                    className={`btn btn-sm w-100 ${currentConfig.descriptionBold ? 'btn-secondary' : 'btn-outline-secondary'}`}
                    onClick={() => updateConfig({ descriptionBold: !currentConfig.descriptionBold })}
                  >
                    Gras
                  </button>
                </div>
                <div className="col-md-3">
                  <button
                    className={`btn btn-sm w-100 ${currentConfig.descriptionItalic ? 'btn-secondary' : 'btn-outline-secondary'}`}
                    onClick={() => updateConfig({ descriptionItalic: !currentConfig.descriptionItalic })}
                  >
                    Italique
                  </button>
                </div>
              </div>

              {/* --- 2. CONFIGURATION PRIX --- */}
              <h6 className="small text-muted border-bottom pb-1 mb-2">Prix</h6>
              <div className="row g-2 align-items-end mb-2">
                 <div className="col-md-4">
                  <label className="form-label small">Couleur</label>
                  <input
                    type="color"
                    className="form-control form-control-color w-100"
                    value={currentConfig.priceColor || '#0d6efd'} 
                    onChange={(e) => updateConfig({ priceColor: e.target.value })}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Taille</label>
                  <select
                    className="form-select"
                    value={currentConfig.priceSize || '1.5rem'} // Equivalent h4 par défaut
                    onChange={(e) => updateConfig({ priceSize: e.target.value })}
                  >
                    <option value="1rem">1rem</option>
                    <option value="1.25rem">1.25rem</option>
                    <option value="1.5rem">1.5rem</option>
                    <option value="2rem">2rem</option>
                    <option value="2.5rem">2.5rem</option>
                    <option value="3rem">3rem</option>
                  </select>
                </div>
                 <div className="col-md-4">
                   <label className="form-label small">Police spécifique</label>
                   <select className="form-select" value={currentConfig.priceFontFamily || 'inherit'} onChange={(e) => updateConfig({ priceFontFamily: e.target.value })}>
                        <option value="inherit">Héritée</option>
                        <option value="Arial, sans-serif">Arial</option>
                        <option value="'Helvetica Neue', Helvetica, sans-serif">Helvetica</option>
                        <option value="'Times New Roman', serif">Times New Roman</option>
                        <option value="Georgia, serif">Georgia</option>
                        <option value="'Courier New', monospace">Courier New</option>
                    </select>
                 </div>
              </div>
              <div className="row g-2 mb-3">
                 <div className="col-12">
                     <div className="btn-group w-100">
                        <button
                            className={`btn btn-sm ${currentConfig.priceBold ? 'btn-secondary' : 'btn-outline-secondary'}`}
                            onClick={() => updateConfig({ priceBold: !currentConfig.priceBold })}
                        >
                            Gras
                        </button>
                        <button
                            className={`btn btn-sm ${currentConfig.priceItalic ? 'btn-secondary' : 'btn-outline-secondary'}`}
                            onClick={() => updateConfig({ priceItalic: !currentConfig.priceItalic })}
                        >
                            Italique
                        </button>
                         <button
                            className={`btn btn-sm ${currentConfig.priceUnderline ? 'btn-secondary' : 'btn-outline-secondary'}`}
                            onClick={() => updateConfig({ priceUnderline: !currentConfig.priceUnderline })}
                        >
                            Souligné
                        </button>
                     </div>
                 </div>
              </div>

              {/* --- 3. CONFIGURATION BOUTON --- */}
              <h6 className="small text-muted border-bottom pb-1 mb-2">Bouton "Ajouter"</h6>
              <div className="row g-2">
                <div className="col-md-6">
                    <label className="form-label small">Couleur Fond</label>
                    <input type="color" className="form-control form-control-color w-100" value={currentConfig.buttonColor || '#0d6efd'} onChange={(e) => updateConfig({ buttonColor: e.target.value })} />
                </div>
                <div className="col-md-6">
                    {/* ✅ Remplacement "Couleur Description (alias)" par "Couleur texte bouton" */}
                    <label className="form-label small">Couleur texte bouton</label>
                    <input type="color" className="form-control form-control-color w-100" value={currentConfig.buttonTextColor || '#ffffff'} onChange={(e) => updateConfig({ buttonTextColor: e.target.value })} />
                </div>
              </div>
            </div>
          </div>

          {/* ── Mode d'affichage & Colonnes ── */}
          <div className="card mb-3 border-light bg-light">
            <div className="card-body">
              <h6 className="card-title text-muted mb-3">Mode d'affichage des produits</h6>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label small">Vue par défaut</label>
                  <div className="d-flex gap-2">
                    {([
                      { value: 'grid', label: '⊞ Grille' },
                      { value: 'list', label: '≡ Liste' },
                      { value: 'carousel', label: '▷ Carousel' },
                    ] as const).map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        className={`btn btn-sm flex-grow-1 ${currentConfig.displayMode === value ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => updateConfig({ displayMode: value })}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Colonnes (mode Grille)</label>
                  <div className="d-flex gap-2">
                    {([2, 3, 4] as const).map(n => (
                      <button
                        key={n}
                        type="button"
                        className={`btn btn-sm flex-grow-1 ${(currentConfig.columnsCount || 3) === n ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => updateConfig({ columnsCount: n })}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Options</label>
                  <div className="d-flex flex-column gap-1">
                    <div className="form-check form-switch mb-0">
                      <input className="form-check-input" type="checkbox" id="showWishlist" checked={currentConfig.showWishlist !== false} onChange={(e) => updateConfig({ showWishlist: e.target.checked })} />
                      <label className="form-check-label small" htmlFor="showWishlist">❤️ Wishlist (favoris)</label>
                    </div>
                    <div className="form-check form-switch mb-0">
                      <input className="form-check-input" type="checkbox" id="enableSort" checked={currentConfig.enableSort !== false} onChange={(e) => updateConfig({ enableSort: e.target.checked })} />
                      <label className="form-check-label small" htmlFor="enableSort">⇅ Tri des produits</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Options d'affichage */}
          <div className="mb-3">
             <div className="form-check form-switch">
                <input className="form-check-input" type="checkbox" id="showSearch" checked={currentConfig.showSearch !== false} onChange={(e) => updateConfig({ showSearch: e.target.checked })} />
                <label className="form-check-label" htmlFor="showSearch">Afficher la recherche</label>
             </div>
             <div className="form-check form-switch">
                <input className="form-check-input" type="checkbox" id="showFilters" checked={currentConfig.showFilters !== false} onChange={(e) => updateConfig({ showFilters: e.target.checked })} />
                <label className="form-check-label" htmlFor="showFilters">Afficher les catégories</label>
             </div>
          </div>
        </div>
      )}

      {activeTab === 'confirmations' && (
        <div className="space-y-4">

          {/* ── Canaux de notification ── */}
          <div className="card mb-4">
            <div className="card-header small fw-bold">📢 <AutoTranslateText sourceLang="fr">Canaux de notification</AutoTranslateText></div>
            <div className="card-body">

              {/* FlexiHub in-app */}
              <div className="mb-3">
                <div className="small fw-bold text-muted mb-2">🔔 <AutoTranslateText sourceLang="fr">Notification interne FlexiHub</AutoTranslateText></div>
                <div className="d-flex flex-wrap gap-4">
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" role="switch" id="ec-notif-flexihub-seller"
                      checked={notif.notifFlexihubSeller !== false}
                      onChange={(e) => updateNotif({ notifFlexihubSeller: e.target.checked })} />
                    <label className="form-check-label small" htmlFor="ec-notif-flexihub-seller">
                      <AutoTranslateText sourceLang="fr">Alerte cloche vendeur (FlexiHub)</AutoTranslateText>
                    </label>
                  </div>
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" role="switch" id="ec-notif-flexihub-client"
                      checked={notif.notifFlexihubClient === true}
                      onChange={(e) => updateNotif({ notifFlexihubClient: e.target.checked })} />
                    <label className="form-check-label small" htmlFor="ec-notif-flexihub-client">
                      <AutoTranslateText sourceLang="fr">Alerte cloche client (FlexiHub)</AutoTranslateText>
                    </label>
                  </div>
                </div>
                <div className="small text-muted mt-1" style={{ fontSize: 11 }}>
                  <AutoTranslateText sourceLang="fr">Apparaît dans la cloche 🔔 du tableau de bord FlexiHub. Le client doit avoir un compte FlexiHub avec le même email.</AutoTranslateText>
                </div>
              </div>

              {/* Email */}
              <div className="mb-3">
                <div className="small fw-bold text-muted mb-2">📧 Email</div>
                <div className="d-flex flex-wrap gap-4">
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" role="switch" id="ec-notif-email-client"
                      checked={notif.emailClient !== false}
                      onChange={(e) => updateNotif({ emailClient: e.target.checked })} />
                    <label className="form-check-label small" htmlFor="ec-notif-email-client">
                      <AutoTranslateText sourceLang="fr">Message de confirmation (client)</AutoTranslateText>
                    </label>
                  </div>
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" role="switch" id="ec-notif-email-seller"
                      checked={notif.emailSeller !== false}
                      onChange={(e) => updateNotif({ emailSeller: e.target.checked })} />
                    <label className="form-check-label small" htmlFor="ec-notif-email-seller">
                      <AutoTranslateText sourceLang="fr">Message de notification (vendeur)</AutoTranslateText>
                    </label>
                  </div>
                </div>
              </div>

              {/* WhatsApp Business */}
              {hasWhatsapp ? (
                <div className="mb-3">
                  <div className="small fw-bold text-muted mb-2">💬 WhatsApp Business (Meta)</div>
                  <div className="d-flex flex-wrap gap-4">
                    <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox" role="switch" id="ec-notif-wa-client"
                        checked={notif.whatsappClient === true}
                        onChange={(e) => updateNotif({ whatsappClient: e.target.checked })} />
                      <label className="form-check-label small" htmlFor="ec-notif-wa-client">
                        <AutoTranslateText sourceLang="fr">WhatsApp client</AutoTranslateText>
                      </label>
                    </div>
                    <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox" role="switch" id="ec-notif-wa-seller"
                        checked={notif.whatsappSeller === true}
                        onChange={(e) => updateNotif({ whatsappSeller: e.target.checked })} />
                      <label className="form-check-label small" htmlFor="ec-notif-wa-seller">
                        <AutoTranslateText sourceLang="fr">WhatsApp vendeur</AutoTranslateText>
                      </label>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="alert alert-light border small mb-0">
                  <AutoTranslateText sourceLang="fr">Pour activer WhatsApp Business, ajoutez vos clés whatsapp_phone_id et whatsapp_token dans Profil → Clés API.</AutoTranslateText>
                </div>
              )}
            </div>
          </div>

          {/* ── Templates WhatsApp ── */}
          {(notif.whatsappClient || notif.whatsappSeller) && (
            <div className="card mb-4">
              <div className="card-header small fw-bold">✏️ <AutoTranslateText sourceLang="fr">Templates WhatsApp</AutoTranslateText></div>
              <div className="card-body">
                <p className="text-muted small mb-3">
                  <AutoTranslateText sourceLang="fr">Variables disponibles :</AutoTranslateText>{' '}
                  {'{{orderId}}'}, {'{{orderTotal}}'}, {'{{customerName}}'}, {'{{shopName}}'}, {'{{paymentMethod}}'}
                </p>
                {notif.whatsappClient && (
                  <div className="mb-3">
                    <label className="form-label small text-muted"><AutoTranslateText sourceLang="fr">Message client</AutoTranslateText></label>
                    <textarea className="form-control form-control-sm" rows={3}
                      value={notif.whatsappClientTemplate || ''}
                      onChange={(e) => updateNotif({ whatsappClientTemplate: e.target.value })} />
                    <div className="form-text">{(notif.whatsappClientTemplate || '').length} / 160 <AutoTranslateText sourceLang="fr">caractères</AutoTranslateText></div>
                  </div>
                )}
                {notif.whatsappSeller && (
                  <div className="mb-3">
                    <label className="form-label small text-muted"><AutoTranslateText sourceLang="fr">Message vendeur</AutoTranslateText></label>
                    <textarea className="form-control form-control-sm" rows={3}
                      value={notif.whatsappSellerTemplate || ''}
                      onChange={(e) => updateNotif({ whatsappSellerTemplate: e.target.value })} />
                    <div className="form-text">{(notif.whatsappSellerTemplate || '').length} / 160 <AutoTranslateText sourceLang="fr">caractères</AutoTranslateText></div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Templates Email ── */}
          <div className="card mb-4">
            <div className="card-header small fw-bold">📧 <AutoTranslateText sourceLang="fr">Templates Email</AutoTranslateText></div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label fw-bold"><AutoTranslateText sourceLang="fr">Modèle d'email</AutoTranslateText></label>
                <select
                  className="form-select"
                  value={emailTemplateType}
                  onChange={(e) => setEmailTemplateType(e.target.value as typeof emailTemplateType)}
                >
                  <option value="buyer_shipping"><AutoTranslateText sourceLang="fr">Confirmation acheteur — Livraison postale</AutoTranslateText></option>
                  <option value="buyer_collect"><AutoTranslateText sourceLang="fr">Confirmation acheteur — Click &amp; Collect</AutoTranslateText></option>
                  <option value="seller"><AutoTranslateText sourceLang="fr">Notification vendeur</AutoTranslateText></option>
                </select>
              </div>

              {emailTemplateType === 'buyer_shipping' && (
                <div>
                  <p className="text-muted small">Variables : {'{{shopName}}'}, {'{{customerName}}'}, {'{{orderId}}'}, {'{{orderTotal}}'}, {'{{orderTax}}'}, {'{{transactionDate}}'}, {'{{paymentMethod}}'}, {'{{deliveryMethod}}'}, {'{{deliveryBlock}}'}, {'{{productList}}'}</p>
                  <textarea
                    className="form-control"
                    rows={10}
                    value={currentConfig.buyerConfirmationEmailTemplate || ''}
                    onChange={(e) => updateConfig({ buyerConfirmationEmailTemplate: e.target.value })}
                  />
                </div>
              )}

              {emailTemplateType === 'buyer_collect' && (
                <div>
                  <p className="text-muted small">Variables : {'{{shopName}}'}, {'{{customerName}}'}, {'{{orderId}}'}, {'{{orderTotal}}'}, {'{{orderTax}}'}, {'{{transactionDate}}'}, {'{{collectAddress}}'}, {'{{mapsLink}}'}, {'{{deliveryBlock}}'}, {'{{productList}}'}</p>
                  <textarea
                    className="form-control"
                    rows={10}
                    value={currentConfig.buyerClickAndCollectEmailTemplate || ''}
                    onChange={(e) => updateConfig({ buyerClickAndCollectEmailTemplate: e.target.value })}
                  />
                </div>
              )}

              {emailTemplateType === 'seller' && (
                <div>
                  <p className="text-muted small">Variables : {'{{shopName}}'}, {'{{customerName}}'}, {'{{customerEmail}}'}, {'{{orderId}}'}, {'{{orderTotal}}'}, {'{{orderTax}}'}, {'{{transactionDate}}'}, {'{{paymentMethod}}'}, {'{{deliveryMethod}}'}, {'{{productList}}'}</p>
                  <textarea
                    className="form-control"
                    rows={10}
                    value={currentConfig.sellerNotificationEmailTemplate || ''}
                    onChange={(e) => updateConfig({ sellerNotificationEmailTemplate: e.target.value })}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="space-y-3">
          <div className="card">
            <div className="card-header"><h6 className="mb-0">Livraison</h6></div>
            <div className="card-body">
              <label className="form-label fw-semibold mb-2">Mode de livraison proposé</label>
              <div className="d-flex flex-column gap-2 mb-3">
                {([
                  { value: 'shipping', label: '🚚 Expédition postale', desc: 'Le client reçoit sa commande par courrier.' },
                  { value: 'click_and_collect', label: '🏪 Click and Collect', desc: 'Le client retire sa commande en boutique.' },
                  { value: 'both', label: '🚚🏪 Expédition postale + Click and Collect', desc: 'Le client choisit entre les deux modes.' },
                ] as const).map(({ value, label, desc }) => (
                  <div
                    key={value}
                    className={`border rounded p-2 cursor-pointer ${(currentConfig.deliveryType || 'shipping') === value ? 'border-primary bg-primary bg-opacity-10' : ''}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => updateConfig({ deliveryType: value })}
                  >
                    <div className="form-check mb-0">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="deliveryType"
                        id={`deliveryType_${value}`}
                        checked={(currentConfig.deliveryType || 'shipping') === value}
                        onChange={() => updateConfig({ deliveryType: value })}
                      />
                      <label className="form-check-label fw-semibold" htmlFor={`deliveryType_${value}`}>{label}</label>
                      <div className="text-muted small">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              {(currentConfig.deliveryType === 'click_and_collect' || currentConfig.deliveryType === 'both') && (
                <>
                  <label className="form-label">Adresse du point de retrait</label>
                  <input
                    type="text"
                    className="form-control mb-2"
                    placeholder="123 rue de la Paix, 75001 Paris"
                    value={currentConfig.clickAndCollect?.address || ''}
                    onChange={(e) => updateConfig({
                      clickAndCollect: {
                        enabled: true,
                        instructions: currentConfig.clickAndCollect?.instructions || '',
                        address: e.target.value
                      }
                    })}
                  />
                  <label className="form-label">Instructions de retrait</label>
                  <input
                    type="text"
                    className="form-control mb-2"
                    placeholder="Présentez votre email de confirmation..."
                    value={currentConfig.clickAndCollect?.instructions || ''}
                    onChange={(e) => updateConfig({
                      clickAndCollect: {
                        enabled: true,
                        address: currentConfig.clickAndCollect?.address || '',
                        instructions: e.target.value
                      }
                    })}
                  />
                </>
              )}
            </div>
          </div>
          <div className="card">
            <div className="card-header"><h6 className="mb-0">Debug</h6></div>
            <div className="card-body">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="debugRenderCount"
                  checked={!!(currentConfig as any).debugRenderCount}
                  onChange={(e) => updateConfig({ debugRenderCount: e.target.checked })}
                />
                <label className="form-check-label" htmlFor="debugRenderCount">
                  Debug render count (console)
                </label>
              </div>
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label">Taux de taxe global (%)</label>
            <input type="number" step="0.1" className="form-control" value={config.globalTaxRate || 0} onChange={(e) => updateConfig({ globalTaxRate: parseFloat(e.target.value) })} />
          </div>
          <div className="mb-3">
            <label className="form-label">Frais d'expédition globaux</label>
            <input type="number" step="0.01" className="form-control" value={config.globalShippingCost || 0} onChange={(e) => updateConfig({ globalShippingCost: parseFloat(e.target.value) })} />
          </div>
        </div>
      )}

      {activeTab === 'legal' && (
        <div>
          <div className="mb-4">
            <label className="form-label fw-bold">CGU / CGV</label>
            {isApiKeyLoading ? <div>Chargement...</div> : !tinymceApiKey ? <div className="alert alert-warning">Clé TinyMCE manquante</div> : (
              <Editor
                apiKey={tinymceApiKey}
                value={currentConfig.termsAndConditions || ''}
                onEditorChange={(content) => updateConfig({ termsAndConditions: content })}
                init={withTabLink({ height: 320, menubar: false, plugins: ['link', 'lists', 'code', 'autolink'], toolbar: 'undo redo | bold italic underline | bullist numlist | link | code' })}
              />
            )}
          </div>
          <div>
            <label className="form-label fw-bold">RGPD</label>
            {isApiKeyLoading ? <div>Chargement...</div> : !tinymceApiKey ? <div className="alert alert-warning">Clé TinyMCE manquante</div> : (
              <Editor
                apiKey={tinymceApiKey}
                value={currentConfig.termsRgpd || ''}
                onEditorChange={(content) => updateConfig({ termsRgpd: content })}
                init={withTabLink({ height: 320, menubar: false, plugins: ['link', 'lists', 'code', 'autolink'], toolbar: 'undo redo | bold italic underline | bullist numlist | link | code' })}
              />
            )}
          </div>
        </div>
      )}

      {/* ── ONGLET VENTES ── */}
      {activeTab === 'sales' && (
        <div>
          {/* Filtres date + boutons */}
          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-end gap-3">
                <div>
                  <label className="form-label small mb-1">Du</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={salesFrom}
                    onChange={e => setSalesFrom(e.target.value)}
                  />
                </div>
                <div>
                  <label className="form-label small mb-1">Au</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={salesTo}
                    onChange={e => setSalesTo(e.target.value)}
                  />
                </div>
                <button className="btn btn-primary btn-sm" onClick={fetchSales} disabled={salesLoading}>
                  {salesLoading ? '⏳ Chargement...' : '🔍 Rechercher'}
                </button>
                {salesOrders.length > 0 && (
                  <button className="btn btn-outline-success btn-sm ms-auto" onClick={exportCsv}>
                    📥 Exporter CSV
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Erreur */}
          {salesError && <div className="alert alert-danger">{salesError}</div>}

          {/* Chiffre d'affaires */}
          {salesOrders.length > 0 && (() => {
            const ca = salesOrders.reduce((sum, o) => sum + (o.orderData?.total ?? 0), 0);
            const paid = salesOrders.filter(o => o.status === 'paid' || o.status === 'shipped' || o.status === 'collected').length;
            return (
              <div className="row g-2 mb-3">
                <div className="col-4">
                  <div className="card text-center border-0 bg-light">
                    <div className="card-body py-2">
                      <div className="fw-bold fs-5">{salesOrders.length}</div>
                      <div className="text-muted small">Commandes</div>
                    </div>
                  </div>
                </div>
                <div className="col-4">
                  <div className="card text-center border-0 bg-light">
                    <div className="card-body py-2">
                      <div className="fw-bold fs-5 text-success">{ca.toFixed(2)} {currentConfig.currency}</div>
                      <div className="text-muted small">Chiffre d'affaires</div>
                    </div>
                  </div>
                </div>
                <div className="col-4">
                  <div className="card text-center border-0 bg-light">
                    <div className="card-body py-2">
                      <div className="fw-bold fs-5 text-primary">{paid}</div>
                      <div className="text-muted small">Payées</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Liste des ventes */}
          {!salesLoading && salesOrders.length === 0 && (
            <div className="text-center text-muted py-4">
              <div style={{ fontSize: 40 }}>📦</div>
              <div>Aucune vente trouvée pour cette période.</div>
            </div>
          )}

          {salesOrders.map(order => {
            const d = order.orderData;
            const isCC = order.deliveryMethod === 'click_and_collect';
            const canShip = !isCC && (order.status === 'pending' || order.status === 'paid');
            const canCollect = isCC && (order.status === 'pending' || order.status === 'paid');
            const busy = actionLoading[order.id];
            const statusBadge: Record<string, string> = {
              pending: 'secondary', paid: 'info', shipped: 'success', collected: 'success',
            };
            const statusLabel: Record<string, string> = {
              pending: 'En attente', paid: 'Payée', shipped: 'Expédiée', collected: 'Récupérée',
            };
            return (
              <div key={order.id} className="card mb-2">
                <div className="card-body py-2 px-3">
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span className="fw-bold small">#{order.id.slice(-8)}</span>
                        <span className={`badge bg-${statusBadge[order.status] || 'secondary'}`}>
                          {statusLabel[order.status] || order.status}
                        </span>
                        <span className="badge bg-light text-dark border">
                          {isCC ? '🏪 Click & Collect' : '🚚 Expédition'}
                        </span>
                      </div>
                      <div className="small text-muted">
                        {new Date(order.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        {' · '}
                        <strong>{d.customerInfo?.name}</strong> ({d.customerInfo?.email})
                        {' · '}
                        <span className="text-success fw-bold">{(d.total ?? 0).toFixed(2)} {currentConfig.currency}</span>
                      </div>
                      <div className="small text-muted mt-1">
                        {(d.items || []).map((i: any) => `${i.product?.name} ×${i.quantity}`).join(', ')}
                      </div>
                    </div>
                    <div className="d-flex flex-column gap-1 align-items-end">
                      {canShip && (
                        <div className="d-flex gap-1 align-items-center">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            style={{ width: 120 }}
                            placeholder="N° suivi (opt.)"
                            value={shipTrackingInputs[order.id] || ''}
                            onChange={e => setShipTrackingInputs(p => ({ ...p, [order.id]: e.target.value }))}
                          />
                          <button
                            className="btn btn-success btn-sm"
                            disabled={busy}
                            onClick={() => handleShip(order)}
                          >
                            {busy ? '⏳' : '🚚 Expédier'}
                          </button>
                        </div>
                      )}
                      {canCollect && (
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={busy}
                          onClick={() => handleCollected(order)}
                        >
                          {busy ? '⏳' : '✅ Marquer récupéré'}
                        </button>
                      )}
                      {order.status === 'shipped' && (
                        <span className="badge bg-success">✔ Expédiée</span>
                      )}
                      {order.status === 'collected' && (
                        <span className="badge bg-success">✔ Récupérée</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {activeTab === 'paypal' && (
        <div className="card p-3">
          <PayPalOrdersPanel shopId={(config as EcommerceConfig).id} pluginType="ecommerce" />
        </div>
      )}
      {activeTab === 'stripe' && (
        <div className="card p-3">
          <StripeOrdersPanel shopId={(config as EcommerceConfig).id} pluginType="ecommerce" />
        </div>
      )}
    </div>
  );
};
